import React from "react";
import { motion } from "framer-motion";
import { MessagesSquare, FlaskConical, Film, ArrowUpRight } from "lucide-react";

const CARDS = [
    {
        id: "debates",
        kind: "Debates and Conversations",
        title: "Real people. Real perspectives.",
        body: "Six Indians. One room. No script. Just the biggest questions facing the country — and the courage to answer them honestly.",
        icon: MessagesSquare,
        accent: "var(--tip-saffron)",
        image:
            "/generated/debate_three_v_three.png",
    },
    {
        id: "experiments",
        kind: "Social Experiments and Documentaries",
        title: "Formats that challenge assumptions.",
        body: "What if strangers from opposite sides of an argument had to find one thing they agreed on? We build the experiments. They bring the honesty.",
        icon: FlaskConical,
        accent: "var(--tip-blue)",
        image:
            "https://images.unsplash.com/photo-1552113125-81af17f36b57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxjaW5lbWF0aWMlMjBkaXZlcnNlJTIwaW5kaWFuJTIweW91dGglMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODA5MzQ5NzJ8MA&ixlib=rb-4.1.0&q=85",
    },
    {
        id: "lifestyle",
        kind: "Entertainment and Lifestyle",
        title: "Stories that capture modern India.",
        body: "From street food to street fashion, music to memes — we showcase the culture, lifestyle and energy of a new generation of Indians.",
        icon: Film,
        accent: "var(--tip-green)",
        image:
            "/generated/beach_hut_party.png",
    },
];

export const WhatWeCreate = ({ onContribute }) => {
    return (
        <section
            id="work"
            data-testid="what-we-create-section"
            className="relative bg-tip-black text-white py-24 md:py-36 overflow-hidden"
        >
            <div className="absolute inset-0 grain opacity-60" />
            <div className="tip-container relative">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
                    <div>
                        <div className="eyebrow text-white/60 mb-6">
                            02 — What We Create
                        </div>
                        <h2 className="font-display font-black text-5xl sm:text-6xl md:text-7xl tracking-[-0.03em] leading-[0.95]">
                            Three formats.
                            <br />
                            One mission.
                        </h2>
                    </div>
                    <p className="max-w-md text-white/65 text-base">
                        Different ways to start the same conversation —
                        unscripted, multilingual, and impossible to look away
                        from.
                    </p>
                </div>

                <div className="grid md:grid-cols-3 gap-px bg-white/15 border border-white/15">
                    {CARDS.map((c, i) => (
                        <motion.article
                            key={c.id}
                            data-testid={`format-card-${c.id}`}
                            initial={{ opacity: 0, y: 28 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.7, delay: i * 0.08 }}
                            className="group relative bg-tip-black p-8 md:p-10 flex flex-col gap-6 min-h-[480px] overflow-hidden hover-zoom"
                        >
                            <div className="aspect-[16/10] w-full overflow-hidden border border-white/10">
                                <img
                                    src={c.image}
                                    alt={c.kind}
                                    className="w-full h-full object-cover"
                                />
                            </div>

                            <div className="flex items-center gap-3">
                                <c.icon
                                    size={20}
                                    strokeWidth={1.5}
                                    style={{ color: c.accent }}
                                />
                                <span
                                    className="eyebrow"
                                    style={{ color: c.accent }}
                                >
                                    {c.kind}
                                </span>
                            </div>

                            <h3 className="font-display font-bold text-3xl md:text-[34px] leading-[1.05] tracking-tight">
                                {c.title}
                            </h3>

                            <div className="mt-auto pt-6 border-t border-white/15 flex items-center justify-between">
                                <span className="text-xs uppercase tracking-[0.2em] text-white/50">
                                    0{i + 1}
                                </span>
                                <ArrowUpRight
                                    size={22}
                                    className="transition-transform duration-500 group-hover:rotate-45"
                                />
                            </div>
                        </motion.article>
                    ))}
                </div>

                <div id="contribute-cta" style={{ scrollMarginTop: "120px" }} className="mt-14 md:mt-16 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-white/15 pt-10">
                    <p className="font-serif-display text-2xl sm:text-3xl md:text-4xl leading-[1.1] max-w-2xl">
                        Have a story, idea or cause worth telling?
                    </p>
                    <button
                        data-testid="whatwecreate-contribute-cta"
                        onClick={onContribute}
                        className="btn-block btn-block-light"
                    >
                        Contribute
                        <ArrowUpRight size={16} strokeWidth={1.6} />
                    </button>
                </div>
            </div>
        </section>
    );
};
